import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function Contact() {
  const [isVisible, setIsVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const location = useLocation();
  const prefilledCollection = (location.state as any)?.collection || '';

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
    
    if (prefilledCollection) {
      setTimeout(() => {
        const messageField = document.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
        if (messageField) {
          messageField.value = `I'm interested in the ${prefilledCollection} collection.`;
        }
      }, 300);
    }
  }, [prefilledCollection]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      alert('Message must be 500 characters or less');
      setFormStatus('idle');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d5s8om86qf5t6pbjhk80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/20"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/39dfe79e3165183092157d0c6ece4fe2.jpg')`,
            objectPosition: 'center'
          }}
        ></div>
        
        <div className={`relative z-10 text-center px-6 max-w-4xl transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-5xl md:text-7xl font-light mb-6 text-white tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Book Your Visit
          </h2>
          <p className="text-lg md:text-xl font-light text-white/90 max-w-2xl mx-auto leading-relaxed">
            Experience our exclusive appointment-only service
          </p>
        </div>
      </section>

      {/* Appointment Section */}
      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <div>
            <p className="text-sm font-light text-[#C9A961] tracking-widest mb-6">EXCLUSIVE EXPERIENCE</p>
            <h3 className="text-4xl md:text-5xl font-light mb-8 text-[#4A4A4A] leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Private Appointment
            </h3>
            <p className="text-lg font-light text-[#6B6B6B] leading-loose mb-8">
              We offer appointment-based private experiences. In a serene and elegant space, our professional consultants will present the unique beauty of each piece and provide personalized recommendations.
            </p>
            
            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center text-[#C9A961]">
                  <i className="ri-time-line text-2xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-light text-[#4A4A4A] mb-2">Flexible Scheduling</h4>
                  <p className="text-sm font-light text-[#6B6B6B] leading-relaxed">
                    Book a time that works best for you. We accommodate your schedule.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center text-[#C9A961]">
                  <i className="ri-user-heart-line text-2xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-light text-[#4A4A4A] mb-2">Personal Consultation</h4>
                  <p className="text-sm font-light text-[#6B6B6B] leading-relaxed">
                    One-on-one guidance from our expert jewelry consultants.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center text-[#C9A961]">
                  <i className="ri-gift-line text-2xl"></i>
                </div>
                <div>
                  <h4 className="text-lg font-light text-[#4A4A4A] mb-2">Exclusive Access</h4>
                  <p className="text-sm font-light text-[#6B6B6B] leading-relaxed">
                    View our complete collection in a private, comfortable setting.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl">
              <h4 className="text-lg font-light text-[#4A4A4A] mb-4">Contact Us Directly</h4>
              <div className="flex flex-col gap-3">
                <a href="https://wa.me/" className="text-sm font-light text-[#6B6B6B] hover:text-[#C9A961] transition-colors duration-300 cursor-pointer flex items-center gap-2">
                  <i className="ri-whatsapp-line text-xl"></i>
                  <span>WhatsApp</span>
                </a>
                <a href="weixin://" className="text-sm font-light text-[#6B6B6B] hover:text-[#C9A961] transition-colors duration-300 cursor-pointer flex items-center gap-2">
                  <i className="ri-wechat-line text-xl"></i>
                  <span>WeChat</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white p-12 rounded-3xl">
            <form className="space-y-6" data-readdy-form id="appointment-form" onSubmit={handleSubmit}>
              <div>
                <input 
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  required
                  className="w-full px-6 py-4 bg-[#FAF8F5] text-[#4A4A4A] text-sm font-light border-none focus:outline-none focus:ring-1 focus:ring-[#C9A961] transition-all duration-300 rounded-2xl"
                />
              </div>
              
              <div>
                <input 
                  type="tel"
                  name="phone"
                  placeholder="Contact Number"
                  required
                  className="w-full px-6 py-4 bg-[#FAF8F5] text-[#4A4A4A] text-sm font-light border-none focus:outline-none focus:ring-1 focus:ring-[#C9A961] transition-all duration-300 rounded-2xl"
                />
              </div>
              
              <div>
                <input 
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  required
                  className="w-full px-6 py-4 bg-[#FAF8F5] text-[#4A4A4A] text-sm font-light border-none focus:outline-none focus:ring-1 focus:ring-[#C9A961] transition-all duration-300 rounded-2xl"
                />
              </div>
              
              <div>
                <input 
                  type="text"
                  name="city"
                  placeholder="City"
                  required
                  className="w-full px-6 py-4 bg-[#FAF8F5] text-[#4A4A4A] text-sm font-light border-none focus:outline-none focus:ring-1 focus:ring-[#C9A961] transition-all duration-300 rounded-2xl"
                />
              </div>
              
              <div>
                <textarea 
                  name="message"
                  placeholder="Special Requests (Optional)"
                  rows={4}
                  maxLength={500}
                  className="w-full px-6 py-4 bg-[#FAF8F5] text-[#4A4A4A] text-sm font-light border-none focus:outline-none focus:ring-1 focus:ring-[#C9A961] transition-all duration-300 resize-none rounded-2xl"
                ></textarea>
              </div>
              
              <button 
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full px-10 py-4 bg-[#C9A961] text-white text-sm font-light tracking-wider hover:bg-[#B89851] transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed rounded-full"
              >
                {formStatus === 'submitting' ? 'SUBMITTING...' : 'SUBMIT APPOINTMENT REQUEST'}
              </button>
              
              {formStatus === 'success' && (
                <p className="text-sm font-light text-green-600">Thank you! We will contact you soon.</p>
              )}
              
              {formStatus === 'error' && (
                <p className="text-sm font-light text-red-600">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&display=swap');
      `}</style>
    </div>
  );
}
